/**
 * Custom element that manages payment options (subscription vs one-time).
 *
 * Markup expected (rendered by the Liquid snippet):
 * <payment-options-selector product-id="1234567890" id="payment-options-selector-1234567890">
 *   <fieldset class="payment-options-selector-container">
 *     <legend>Payment Options</legend>
 *     <div class="payment-options-selector-option">
 *       <label class="payment-options-selector-label">
 *         <input type="radio" name="payment-option" id="subscription-option" checked>
 *         $X.XX / Every Y
 *       </label>
 *     </div>
 *     <div class="payment-options-selector-option">
 *       <label class="payment-options-selector-label">
 *         <input type="radio" name="payment-option" id="one-time-option">
 *         $X.XX / N units
 *       </label>
 *     </div>
 *   </fieldset>
 * </payment-options-selector>
 *
 * Events (bubble + composed) emitted on any change:
 * - payment-options:change { detail: { value, isSubscription, isOneTime, productId } }
 *
 * Integration expectations:
 * - Disables/enables the related <delivery-frequency-dropdown> when switching between
 *   one-time (disabled) and subscription (enabled).
 */
class PaymentOptionsSelector extends HTMLElement {
  static get observedAttributes() {
    return ['disabled'];
  }

  constructor() {
    super();
    this._elements = {};
    this.productId = this.getAttribute('product-id') || null;
    this.handleRadioChange = this.handleRadioChange.bind(this);
    this.handleDeliveryFrequencyChange = this.handleDeliveryFrequencyChange.bind(this);
    this.deliveryFrequencyDropdown = null;
    this.deliveryFrequencyObserver = null;
    this.deliveryFrequencyDefinitionPromise = null;
    this.hasAttachedDeliveryFrequencyListener = false;
  }

  connectedCallback() {
    this._elements.fieldset = this.querySelector('.payment-options-selector-container') || this;
    this._elements.oneTimeRadio = this.querySelector('#one-time-option');
    this._elements.subscriptionRadio = this.querySelector('#subscription-option');
    this._elements.oneTimeOption = this.querySelector('#one-time-option-container');
    this._elements.subscriptionOption = this.querySelector('#subscription-option-container');
    this._elements.radios = Array.from(this.querySelectorAll('input[type="radio"][name="payment-option"]'));
    this._elements.subscriptionLabelText = this.querySelector('.payment-options-selector-label-text');
    this._elements.discountPercentage = this.querySelector('#subscription-discount-percentage');
    this.discountTemplate = this._elements.discountPercentage?.dataset.discountTemplate || '';

    if (this._elements.subscriptionRadio) {
      this._elements.subscriptionRadio.addEventListener('change', this.handleRadioChange);
    }
    if (this._elements.oneTimeRadio) {
      this._elements.oneTimeRadio.addEventListener('change', this.handleRadioChange);
    }

    this.initializeFromDeliveryFrequency();
    this.updateDisabledState();
    // Initialize state from current selection and emit initial event for listeners
    this.syncFromInputs(true);
  }

  disconnectedCallback() {
    if (this._elements.subscriptionRadio) {
      this._elements.subscriptionRadio.removeEventListener('change', this.handleRadioChange);
    }
    if (this._elements.oneTimeRadio) {
      this._elements.oneTimeRadio.removeEventListener('change', this.handleRadioChange);
    }
    if (this.deliveryFrequencyObserver) {
      this.deliveryFrequencyObserver.disconnect();
      this.deliveryFrequencyObserver = null;
    }
    if (this.deliveryFrequencyDropdown && this.hasAttachedDeliveryFrequencyListener) {
      this.deliveryFrequencyDropdown.removeEventListener(
        'delivery-frequency:change',
        this.handleDeliveryFrequencyChange
      );
      this.hasAttachedDeliveryFrequencyListener = false;
    }
  }

  attributeChangedCallback(name, _oldValue, _newValue) {
    if (!this.isConnected) return;
    if (name === 'disabled') this.updateDisabledState();
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(value) {
    if (value) this.setAttribute('disabled', '');
    else this.removeAttribute('disabled');
  }

  get value() {
    if (this._elements.subscriptionRadio?.checked) return 'subscription';
    if (this._elements.oneTimeRadio?.checked) return 'one-time';
    // Fallback to subscription if present
    return this._elements.subscriptionRadio ? 'subscription' : 'one-time';
  }

  set value(val) {
    const normalized = val === 'one-time' ? 'one-time' : 'subscription';
    if (this._elements.subscriptionRadio) {
      this._elements.subscriptionRadio.checked = normalized === 'subscription';
    }
    if (this._elements.oneTimeRadio) {
      this._elements.oneTimeRadio.checked = normalized === 'one-time';
    }
    this.syncFromInputs(true);
  }

  handleRadioChange() {
    if (this.disabled) return;
    this.syncFromInputs(true);
  }

  syncFromInputs(emit = true) {
    this.updateSelectedState();
    // Keep related controls (like delivery frequency) in sync
    this.updateExternalDependencies();
    if (emit) this.emitChange();
  }

  updateSelectedState() {
    this._elements.subscriptionOption?.classList.toggle(
      'is-selected',
      Boolean(this._elements.subscriptionRadio?.checked)
    );
    this._elements.oneTimeOption?.classList.toggle(
      'is-selected',
      Boolean(this._elements.oneTimeRadio?.checked)
    );
  }

  updateExternalDependencies() {
    // Disable delivery-frequency dropdown when "one-time" is selected
    const dropdownId = this.productId ? `delivery-frequency-dropdown-${this.productId}` : null;
    const dropdownEl = dropdownId ? document.getElementById(dropdownId) : null;
    if (!dropdownEl) return;

    const isOneTime = this.value === 'one-time';
    dropdownEl.disabled = isOneTime;
  }

  updateDisabledState() {
    const isDisabled = this.disabled;
    if (this._elements.fieldset) {
      this._elements.fieldset.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    }
    if (this._elements.radios?.length) {
      this._elements.radios.forEach((radio) => {
        radio.disabled = isDisabled;
      });
    }
  }

  resolveDeliveryFrequencyDropdown() {
    const dropdownId = this.productId ? `delivery-frequency-dropdown-${this.productId}` : null;
    if (!dropdownId) return null;

    const dropdown = document.getElementById(dropdownId);
    this.deliveryFrequencyDropdown = dropdown || null;

    if (dropdown && !this.hasAttachedDeliveryFrequencyListener) {
      dropdown.addEventListener('delivery-frequency:change', this.handleDeliveryFrequencyChange);
      this.hasAttachedDeliveryFrequencyListener = true;
    }

    return this.deliveryFrequencyDropdown;
  }

  watchForDeliveryFrequencyDropdown() {
    const dropdownId = this.productId ? `delivery-frequency-dropdown-${this.productId}` : null;
    const observationRoot = this.closest('.shopify-section') || this.parentNode;

    if (this.deliveryFrequencyObserver || !observationRoot || !dropdownId) {
      return;
    }

    this.deliveryFrequencyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          const matchingDropdown =
            node.id === dropdownId
              ? node
              : node.querySelector?.(`#${CSS.escape(dropdownId)}`);

          if (matchingDropdown) {
            this.initializeFromDeliveryFrequency();
            return;
          }
        }

        if (mutation.type === 'childList' && mutation.target instanceof Element) {
          const matchingDropdown = mutation.target.querySelector?.(`#${CSS.escape(dropdownId)}`);
          if (matchingDropdown) {
            this.initializeFromDeliveryFrequency();
            return;
          }
        }
      }
    });

    this.deliveryFrequencyObserver.observe(observationRoot, {
      childList: true,
      subtree: true
    });
  }

  waitForDeliveryFrequencyDefinition() {
    if (typeof customElements === 'undefined' || this.deliveryFrequencyDefinitionPromise) {
      return;
    }

    this.deliveryFrequencyDefinitionPromise = customElements
      .whenDefined('delivery-frequency-dropdown')
      .then(() => {
        if (!this.isConnected) return;
        console.log('[payment-options-selector] delivery-frequency-dropdown definition is ready', {
          productId: this.productId
        });
        this.initializeFromDeliveryFrequency();
      });
  }

  /**
   * To calculate the discount percentage, we need to get the current selection from the delivery frequency dropdown on page load.
   * @returns 
   */
  initializeFromDeliveryFrequency() {
    const dropdown = this.resolveDeliveryFrequencyDropdown();

    if (!dropdown || typeof dropdown.getCurrentSelection !== 'function') {
      this.watchForDeliveryFrequencyDropdown();
      this.waitForDeliveryFrequencyDefinition();
      return;
    }

    if (this.deliveryFrequencyObserver) {
      this.deliveryFrequencyObserver.disconnect();
      this.deliveryFrequencyObserver = null;
    }

    const initialSelection = dropdown.getCurrentSelection();
    this.updateFromDeliveryFrequency(initialSelection);
  }

  updateFromDeliveryFrequency(detail) {
    if (!detail) {
      return;
    }

    const { label, price } = detail;

    if (this._elements.subscriptionLabelText && label && price) {
      this._elements.subscriptionLabelText.textContent = `${price} / ${label}`;
    }

    if (this._elements.discountPercentage) {
      const discountPercentage = this.calculateDiscountPercentage(detail);
      this._elements.discountPercentage.textContent =
        discountPercentage > 0 && this.discountTemplate.includes('XX')
          ? this.discountTemplate.replace(/XX/g, discountPercentage)
          : this.discountTemplate; // Returns just the template if the discount percentage is not greater than 0 or if the discount template does not contain 'XX'
    }
  }

  /**
   * Handles 'delivery-frequency:change' from the associated dropdown.
   * This is a downstream sync only: delivery frequency changes can affect the
   * subscription label and discount display, but they do not change which
   * payment option is selected. Re-emitting payment-options:change here causes
   * a feedback loop with the dropdown.
   */
  handleDeliveryFrequencyChange(event) {
    this.updateFromDeliveryFrequency(event?.detail);
  }

  /**
   * Calculates the discount percentage based on the product original price, the selected price, and the absorbency quantity.
   * @param {Object} deliveryFrequencyDetail - The delivery frequency event detail payload
   * @returns {number} The discount percentage
   */
  calculateDiscountPercentage(deliveryFrequencyDetail) {
    const { 
      productBasePriceInt: productOriginalPrice, 
      maxQuantityMultiplier: absorbencyQuantity, 
      productSelectedPriceInt: price,
      productBaseQuantity: minQuantity,
    } = deliveryFrequencyDetail || {};

    if (productOriginalPrice && price && absorbencyQuantity && minQuantity) {
      let discount = (productOriginalPrice / minQuantity - price / absorbencyQuantity) / productOriginalPrice * minQuantity;
      const roundedDiscount = Math.round(parseFloat((discount * 100)));
      return roundedDiscount;
    }
    return 0;
  }

  emitChange() {
    const value = this.value;
    const detail = {
      value,
      isSubscription: value === 'subscription',
      isOneTime: value === 'one-time',
      productId: this.productId
    };
    this.dispatchEvent(
      new CustomEvent('payment-options:change', {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }
}

if (!customElements.get('payment-options-selector')) {
  customElements.define('payment-options-selector', PaymentOptionsSelector);
}

// Example listener (optional; safe no-op if element not present)
document.addEventListener('DOMContentLoaded', () => {
  const el = document.querySelector('payment-options-selector');
  if (!el) return;
  el.addEventListener('payment-options:change', (event) => {
    console.log('Payment option updated', event.detail);
  });
});
